import {
  ShipmentStatus,
  ShipmentTrackingStatus,
  type ShipmentDTO,
} from '@invenco/shipping-interface';

import { formatDate } from '@/shared/helpers';
import { shipmentTrackingStatusTitle } from '@/shared/title-maps';

export type FulfillmentStageStatus = 'not-started' | 'in-progress' | 'completed';
export type FulfillmentStage = {
  key: 'routing' | 'fulfillment' | 'delivery';
  label: string;
  status: FulfillmentStageStatus;
  description?: string;
};

export function deriveFulfillmentStages(shipments: ShipmentDTO[]): FulfillmentStage[] {
  const activeShipments = shipments.filter(
    (shipment) => shipment.status !== ShipmentStatus.CANCELLED,
  );
  const hasShipment = activeShipments.length > 0;
  const allShipped =
    hasShipment && activeShipments.every((shipment) => shipment.status === ShipmentStatus.SHIPPED);
  const allDelivered =
    allShipped &&
    activeShipments.every(
      (shipment) => shipment.trackingStatus === ShipmentTrackingStatus.DELIVERED,
    );
  const deliveryTrackingStatus = activeShipments.find(
    (shipment) => shipment.trackingStatus !== ShipmentTrackingStatus.DELIVERED,
  )?.trackingStatus;

  // Completed Routing/Fulfillment read "Completed on <date>": routing off the first shipment's
  // creation, fulfillment off the last shipment's dispatch. Delivery has no equivalent date
  // without carrier tracker timestamps, so it stays description-less once complete.
  const routedAt = earliestCreatedAt(activeShipments);
  const shippedAt = latestShippedAt(activeShipments);

  return [
    {
      key: 'routing',
      label: 'Routing',
      status: hasShipment ? 'completed' : 'in-progress',
      description: hasShipment ? completedOn(routedAt) : 'In progress',
    },
    {
      key: 'fulfillment',
      label: 'Fulfillment',
      status: allShipped ? 'completed' : hasShipment ? 'in-progress' : 'not-started',
      description: allShipped
        ? completedOn(shippedAt)
        : hasShipment
          ? 'In progress'
          : 'Not started',
    },
    {
      key: 'delivery',
      label: 'Delivery',
      status: allDelivered ? 'completed' : allShipped ? 'in-progress' : 'not-started',
      description: allDelivered
        ? undefined
        : allShipped
          ? deliveryTrackingStatus
            ? shipmentTrackingStatusTitle[deliveryTrackingStatus]
            : 'In progress'
          : 'Not started',
    },
  ];
}

/** Latest dispatch date across the given shipments; also used for the card's "Date shipped". */
export function latestShippedAt(shipments: ShipmentDTO[]) {
  return shipments.reduce<string | undefined>((latest, shipment) => {
    if (shipment.status !== ShipmentStatus.SHIPPED || !shipment.shippedAt) return latest;

    return !latest || shipment.shippedAt > latest ? shipment.shippedAt : latest;
  }, undefined);
}

function earliestCreatedAt(shipments: ShipmentDTO[]) {
  return shipments.reduce<string | undefined>(
    (earliest, shipment) =>
      !earliest || shipment.createdAt < earliest ? shipment.createdAt : earliest,
    undefined,
  );
}

function completedOn(date?: string) {
  return date ? `Completed on ${formatDate(date)}` : undefined;
}
